import {createApp} from 'vue'

import App from './App.vue'
import storageLocal from "@/api/storage/local";
import storageSession from "@/api/storage/session";
import router from '@/router'
import store from '@/store'
import {i18n} from "@/i18n/i18n";
import plugin_maska from '@/plugins/maska'
import {browserTracingIntegration, replayIntegration} from '@sentry/vue'
import * as Sentry from '@sentry/vue'

import mixins_base from "@/mixins/base";
import mixins_breadcrumbs from "@/mixins/breadcrumbs";

import directive_dropdown from "@/directives/dropdown";

import plugin_platform from "@/plugins/platform";
import plugin_order from "@/plugins/order";
import plugin_delivery from "@/plugins/delivery";
import plugin_auth from "@/plugins/auth";
import plugin_toast from "@/plugins/toast";
import plugin_modal from "@/plugins/modal";
import plugin_broadcast from "@/plugins/broadcast";
import plugin_activity from "@/plugins/activity";

import {errorHandler} from "@/errors";

import Loading from "@/components/Loading.vue";
import Spinner from "@/components/Spinner.vue";
import Modals from "@/components/Modals.vue";

// Partner theme and skin styles generated by whitelabel/scss.js. Keep it the last import: its CSS must come after the
// component styles, as it did when the skins were separate webpack entries.
import "virtual:whitelabel-styles";

const app = createApp(App)
    .use(store)
    .use(router)
    .use(plugin_platform, {
        baseURL: process.env.VUE_APP_PLATFORM_API_URL,
        params: {
            partner_id: process.env.VUE_APP_PARTNER_ID,
        }
    })
    .use(plugin_order, {
        baseURL: process.env.VUE_APP_ORDER_API_URL,
        params: {
            partner_id: process.env.VUE_APP_PARTNER_ID,
        }
    })
    .use(plugin_delivery, {
        baseURL: process.env.VUE_APP_DELIVERY_API_URL,
        params: {
            partner_id: process.env.VUE_APP_PARTNER_ID,
        }
    })
    .use(plugin_auth, {
        baseURL: process.env.VUE_APP_AUTH_API_URL,
        params: {
            partner_id: process.env.VUE_APP_PARTNER_ID,
        }
    })
    .use(storageLocal, {
        namespace: process.env.VUE_APP_STORAGE_NAMESPACE,
    })
    .use(storageSession, {
        namespace: process.env.VUE_APP_STORAGE_NAMESPACE,
    })
    .use(i18n)
    .use(plugin_toast)
    .use(plugin_modal, {store})
    .use(plugin_maska)
    .use(plugin_broadcast)
    .use(plugin_activity)

    .mixin(mixins_base)
    .mixin(mixins_breadcrumbs)

    .component('loading', Loading)
    .component('spinner', Spinner)
    .component('modals', Modals)

    .directive('dropdown', directive_dropdown);

app.config.errorHandler = errorHandler;

if (process.env.VUE_APP_SENTRY_DSN && 'localhost' !== window.location.hostname) {
    Sentry.init({
        app,
        dsn: process.env.VUE_APP_SENTRY_DSN,
        // Do not send PII to Sentry (replaces sendDefaultPii: false from SDK v10, whose v11 defaults collect it):
        // userInfo controls automatic IP address inference and user fields, cookies the cookies of captured requests
        dataCollection: {
            userInfo: false,
            cookies: false,
        },
        release: `app@${process.env.VUE_APP_VERSION}`,
        tracesSampleRate: parseFloat(process.env.VUE_APP_SENTRY_TRACES_SAMPLE_RATE),
        replaysSessionSampleRate: parseFloat(process.env.VUE_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE),
        replaysOnErrorSampleRate: parseFloat(process.env.VUE_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE),
        integrations: [
            browserTracingIntegration({
                router: router,
            }),
            replayIntegration(),
        ],
        beforeSend(event) {
            // Drop known noisy errors:
            const msg =
                event.exception?.values?.[0]?.type + ':' + event.exception?.values?.[0]?.value
            if (msg && /ResizeObserver loop|Blocked a frame/i.test(msg)) return null
            return event
        },
        ignoreErrors: [
            /ResizeObserver loop/i,
            /NetworkError when attempting to fetch resource/i,
            /AxiosError: Network Error/i,
            /Network Error/i,
            /Request failed with status code 401/i, // ignore 401 errors
            /Missing required param "grouping"/i // checkout page known issue
        ],
        denyUrls: [
            /extensions\//i, // browser extensions
            /^chrome:\/\//i,
            /safari-web-extension/i,
        ],
    })
}

app.mount('#app');

window.PORTAL_VERSION = process.env.VUE_APP_VERSION;